import React, { ReactNode } from 'react';
import { css } from '@emotion/css';
import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import ValuationPng from './smartphone-with-valuation.png';
import ValuationWebp from './smartphone-with-valuation.webp';
import ValuationPngMobile from './smartphone-with-valuation-mobile.png';
import ValuationWebpMobile from './smartphone-with-valuation-mobile.webp';
import HousePng from './house.png';
import HouseWebp from './house.webp';
import ChartsPng from './charts.png';
import ChartsWebp from './charts.webp';
import RepeatPng from './repeat.png';
import RepeatWebp from './repeat.webp';
import { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { APP_CONFIG } from '../../../../../config';
import { OrderedList } from '../../../../common/display/orderedList/OrderedList';
import { ImageSource } from '../../../../common/display/imageSource/ImageSource';
import { report } from '../../../../../tracking/tracking';
import { TrackingEvents } from '../../../../../tracking/trackingEvents';
import { useMembership } from '../../../../../customHooks/useMembership';
import { Loader } from '../../../../common/display/loader/Loader';
import { useValuations } from '../../../../../customHooks/useValuations';
import { useGetDiscoverKaeuferPlusLink } from '../../../../../customHooks/useGetDiscoverKaeuferPlusLink.hook';
import { Msg, useIntl } from '@/i18n';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const styles = {
  overviewTab: {
    maxWidth: 530,
    '@media (max-width: 467px)': {
      marginBottom: 52,
    },
  },
  buttonAreaBasicButtonOne: {
    width: 250,
    margin: '0 8px',
    '@media (max-width: 420px)': {
      width: 285,
      display: 'block',
      margin: '0 auto 8px',
    },
  },
  buttonAreaBasicButtonTwo: {
    width: 250,
    margin: '0 8px',
    '@media (max-width: 420px)': {
      width: 285,
      display: 'block',
      margin: '8px auto 0',
    },
  },
  contingent: {
    width: 450,
    margin: 'auto',
    '@media (max-width: 420px)': {
      width: 285,
      display: 'block',
    },
  },
};

const OverviewTab = () => {
  return (
    <div className={css(styles.overviewTab)}>
      <div className='font-heading-xsmall-regular padding-bottom-l'>
        <Msg id='benefits.valuation.modal.overview.paragraph1' defaultMessage='You have found an interesting...' />
      </div>
      <div className='padding-bottom-l'>
        <Msg id='benefits.valuation.modal.overview.paragraph2' defaultMessage='To start secured into the next...' />
      </div>
      <ul className='list-check list-spacing-s'>
        <li>
          <Msg id='benefits.valuation.modal.overview.bullet1' defaultMessage='Valuation in less minutes' />
        </li>
        <li>
          <Msg id='benefits.valuation.modal.overview.bullet2' defaultMessage='Outlook for the next two years' />
        </li>
        <li>
          <Msg
            id='benefits.valuation.modal.overview.bullet3'
            defaultMessage='Exclusive and free for BuyerPlus'
            values={{
              BuyerPlusLogo: <PlusBadge variant='buy' />,
            }}
          />
        </li>
      </ul>
    </div>
  );
};

const HowItWorksTab = () => {
  const { t } = useIntl();
  const Item = OrderedList.Item;

  return (
    <div className='margin-bottom-xxl'>
      <OrderedList>
        <Item
          image={
            <ImageSource
              image={HousePng}
              imageWebp={HouseWebp}
              imageAlt={t({
                id: 'benefits.valuation.modal.howItWorks.image1',
                defaultMessage: 'house',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.valuation.modal.howItWorks.paragraph1',
            defaultMessage: 'Get your valuation',
          })}
        />
        <Item
          image={
            <ImageSource
              image={ChartsPng}
              imageWebp={ChartsWebp}
              imageAlt={t({
                id: 'benefits.valuation.modal.howItWorks.image2',
                defaultMessage: 'charts',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.valuation.modal.howItWorks.paragraph2',
            defaultMessage: 'Get approximate valuation for the next two years',
          })}
        />
        <Item
          image={
            <ImageSource
              image={RepeatPng}
              imageWebp={RepeatWebp}
              imageAlt={t({
                id: 'benefits.valuation.modal.howItWorks.image3',
                defaultMessage: 'repeat sign',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.valuation.modal.howItWorks.paragraph3',
            defaultMessage: 'Update your valuation every three months',
          })}
        />
      </OrderedList>
    </div>
  );
};

const OverviewButton = ({ isBuyerPlus }: { isBuyerPlus: boolean }) => (
  <a
    className={`button-primary ${css(styles.buttonAreaBasicButtonOne)}`}
    href={APP_CONFIG.valuationUrl}
    onClick={(e) => {
      e.preventDefault();
      report(TrackingEvents.VALUATION.OVERVIEW_CLICKED, isBuyerPlus);
      location.href = e.currentTarget.href;
    }}
  >
    <Msg id='benefits.valuation.modal.overviewButton' defaultMessage='My valuations' />
  </a>
);

const ButtonArea = ({
  valuationsCount,
  isBuyerPlus,
  remainingValuations,
}: {
  valuationsCount?: number;
  isBuyerPlus?: boolean;
  remainingValuations?: number;
}) => {
  const discoverKaeuferPlusLink = useGetDiscoverKaeuferPlusLink({ ctaType: 'valuation' });

  if (isBuyerPlus === undefined || remainingValuations === undefined || valuationsCount === undefined) {
    return <Loader size='small' />;
  }

  return (
    <div className='align-center'>
      {valuationsCount > 0 && <OverviewButton isBuyerPlus={isBuyerPlus} />}
      {isBuyerPlus && remainingValuations > 0 && (
        <a
          className={`button-secondary ${css(styles.buttonAreaBasicButtonTwo)}`}
          href={`${APP_CONFIG.valuationUrl}/bewerten`}
          onClick={(e) => {
            e.preventDefault();
            report(TrackingEvents.VALUATION.WIZARD_CLICKED, isBuyerPlus);
            location.href = e.currentTarget.href;
          }}
        >
          <Msg id='benefits.valuation.modal.linkButton' defaultMessage='Evaluate real estate' />
        </a>
      )}
      {!isBuyerPlus && (
        <a className={`button-secondary ${css(styles.buttonAreaBasicButtonTwo)}`} href={discoverKaeuferPlusLink}>
          <Msg id='benefits.valuation.modal.unlockBuyerPlusButton' defaultMessage='Unlock buyer plus' />
        </a>
      )}
      {isBuyerPlus && valuationsCount === 0 && remainingValuations === 0 && (
        <div className={`align-center ${css(styles.contingent)}`}>
          <Msg
            id='benefits.valuation.modal.contingent'
            defaultMessage='No contingent left and no valuation is present'
            values={{
              b: (children: ReactNode) => <b>{children}</b>,
            }}
          />
        </div>
      )}
    </div>
  );
};

export const Valuation = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const { valuationsCount } = useValuations();
  const { isBuyerPlus, remainingValuations } = useMembership();

  return (
    <Modal
      title={t({ id: 'benefits.valuation.headline', defaultMessage: 'Real estate valuation' })}
      isVisible={isVisible}
      image={ValuationPng}
      imageWebp={ValuationWebp}
      imageMobile={ValuationPngMobile}
      imageMobileWebp={ValuationWebpMobile}
      imageUseFullWidth={false}
      imageAlt={t({
        id: 'benefits.valuation.modal.image',
        defaultMessage: 'Smartphone display shows house and valuation data',
      })}
      tabsContent={[
        <OverviewTab key='benefits.valuation.modal.overview' />,
        <HowItWorksTab key='benefits.valuation.modal.howItWorks' />,
      ]}
      buttonArea={
        <ButtonArea
          valuationsCount={valuationsCount}
          isBuyerPlus={isBuyerPlus}
          remainingValuations={remainingValuations}
        />
      }
      benefit={Benefit.VALUATION}
      onClose={onClose}
    />
  );
};
